import { campaignAnalyticsGranularitySchema } from "@adpilot/shared";
import { IconBrandAmazon } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { DashboardOverviewSections } from "@/components/dashboard/dashboard-overview-sections";
import { DashboardFrame } from "@/components/dashboard/dashboard-page-layout";
import {
  recentRunStatusesByCampaign,
  summarizeAiActivity,
} from "@/components/dashboard/overview/ai-activity";
import { AiStatusCard } from "@/components/dashboard/overview/ai-status-card";
import { AttentionCard, buildAttentionItems } from "@/components/dashboard/overview/attention-card";
import { ConversionEfficiencyCard } from "@/components/dashboard/overview/conversion-efficiency-card";
import {
  periodDayCount,
  useShopMarketplaceCodes,
} from "@/components/dashboard/overview/overview-format";
import { ShopComparisonSection } from "@/components/dashboard/overview/shop-comparison-card";
import {
  TopAsinsCard,
  topAsinRowsFromProductCenter,
} from "@/components/dashboard/overview/top-asins-card";
import {
  buildTopCampaignRows,
  TopCampaignsCard,
} from "@/components/dashboard/overview/top-campaigns-card";
import {
  useOverviewPendingProposals,
  useOverviewRecentRuns,
  useOverviewTopAsins,
  useWorkspaceAiPolicySummary,
} from "@/components/dashboard/overview/use-overview-data";
import { WorkspaceAiPolicyDialog } from "@/components/dashboard/workspace-ai-policy/workspace-ai-policy-dialog";
import { HeaderScopePortal } from "@/components/layout/header-scope-slot";
import { ShopScopeSelect } from "@/components/layout/shop-scope-select";
import { ConnectAmazonDialog } from "@/components/settings/ConnectAmazonDialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { useDashboardDataFreshness } from "@/hooks/use-dashboard-data-freshness";
import { useDashboardPerformance } from "@/hooks/use-dashboard-performance";
import { useMinuteNow } from "@/hooks/use-minute-now";
import { usePermission } from "@/hooks/use-permission";
import { useShopScope } from "@/hooks/use-shop-scope";
import { useScopeAccountSplit } from "@/hooks/use-vendor-metric-availability";
import { asinFilterSearchSchema } from "@/lib/asin-filter-search";
import { fetchDashboardSummary } from "@/lib/dashboard-api";
import { resolveDashboardConnection } from "@/lib/dashboard-sales";
import { requireModule } from "@/lib/module-access";
import { canManageAmazonForActiveWorkspace } from "@/lib/workspace-access";
import { useAuthStore } from "@/stores/auth";
import { resolveReportingPeriod, useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

// AI Operations opens the same policy dialog and imports it from this route module.
export { WorkspaceAiPolicyDialog };

export const Route = createFileRoute("/_authed/")({
  beforeLoad: () => {
    requireModule("dashboard");
  },
  validateSearch: z.object({
    compare: z.enum(["previous_period", "off"]).catch("previous_period"),
    granularity: campaignAnalyticsGranularitySchema.catch("day"),
    // ASIN filter: applied child ASINs (a parent is stored as its variations).
    asins: asinFilterSearchSchema.catch([]),
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { hasPermission, hasModule } = usePermission();
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // Applied ASIN filter: narrows the performance, campaign, ASIN and shop reads. The AI
  // card keeps shop-wide numbers (the summary's aiSummary ignores `asins`; runs and
  // proposals are not ASIN-scoped).
  const asins = search.asins;
  const [aiPolicyOpen, setAiPolicyOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const currentShop = useAuthStore((state) =>
    state.shops.find((shop) => shop.shopId === state.currentShopId),
  );
  // ADP-110: the analytics scope (multi-select, default all workspace shops)
  // drives the aggregated summary; the sorted set keys the query so a scope
  // change refetches.
  const { scopeShopIds, workspaceShops } = useShopScope();
  // Vendor (1P) shops in the scope have no Seller Central data yet (#679).
  const accounts = useScopeAccountSplit(scopeShopIds);
  const now = useMinuteNow();
  const resolvedPeriod = resolveReportingPeriod(range, now, timeZone);
  const frozenPeriod = { type: "custom" as const, ...resolvedPeriod };
  const dashboardScope = {
    workspaceId: user?.activeWorkspaceId ?? null,
    shopIds: scopeShopIds,
    ...resolvedPeriod,
    timeZone,
    granularity: search.granularity,
    asins,
  };
  // Also refetches the performance reads when the data-status stream reports new data.
  const freshnessQuery = useDashboardDataFreshness(dashboardScope);
  const { ads: analyticsQuery, sales: salesQuery } = useDashboardPerformance(dashboardScope);
  const { data, isLoading, isError, errorUpdatedAt, refetch } = useQuery({
    queryKey: [
      "dashboard",
      "summary",
      user?.activeWorkspaceId,
      scopeShopIds.join(","),
      resolvedPeriod.startDate,
      resolvedPeriod.endDate,
      timeZone,
      asins.join(","),
    ],
    queryFn: () => fetchDashboardSummary(frozenPeriod, timeZone, scopeShopIds, asins),
    enabled: scopeShopIds.length > 0,
  });

  const runsQuery = useOverviewRecentRuns(scopeShopIds, timeZone);
  const proposalsQuery = useOverviewPendingProposals(scopeShopIds);
  const aiPolicy = useWorkspaceAiPolicySummary(data?.aiMasterOn);
  const topAsinsQuery = useOverviewTopAsins({ scopeShopIds, ...resolvedPeriod, asins });
  const marketplaceByShop = useShopMarketplaceCodes();
  const activity = summarizeAiActivity(runsQuery.data?.runs ?? [], now, timeZone);

  useEffect(() => {
    if (isError && data && errorUpdatedAt > 0) toast.error(t("loadFailed"));
  }, [data, errorUpdatedAt, isError, t]);

  const [connectAmazonOpen, setConnectAmazonOpen] = useState(false);
  const handleConnect = () => setConnectAmazonOpen(true);
  // Rendered as the second child of both returns below: activating the first
  // Shop flips this page out of its empty state, and the dialog must survive
  // that switch to show its finish step.
  const connectAmazonDialog = (
    <ConnectAmazonDialog open={connectAmazonOpen} onOpenChange={setConnectAmazonOpen} />
  );

  const canConnect = canManageAmazonForActiveWorkspace(user);
  const connection = resolveDashboardConnection(data?.connected, salesQuery.data?.connected);

  if (scopeShopIds.length === 0 || connection === "connect") {
    return (
      <>
        <DashboardFrame>
          <HeaderScopePortal>
            <ShopScopeSelect />
          </HeaderScopePortal>
          <EmptyState
            icon={<IconBrandAmazon className="size-16" />}
            title={t("connect.title")}
            description={canConnect ? t("connect.description") : t("connect.memberDescription")}
            actionLabel={canConnect ? t("connect.action") : undefined}
            onAction={canConnect ? handleConnect : undefined}
          />
        </DashboardFrame>
        {connectAmazonDialog}
      </>
    );
  }

  return (
    <>
      <DashboardFrame>
        <HeaderScopePortal>
          <ShopScopeSelect />
        </HeaderScopePortal>
        <DashboardOverviewSections
          analytics={analyticsQuery.data}
          salesTraffic={salesQuery.data}
          salesLoading={salesQuery.isLoading}
          salesFetching={salesQuery.isFetching}
          salesError={salesQuery.isError}
          onSalesRetry={() => void salesQuery.refetch()}
          startDate={resolvedPeriod.startDate}
          endDate={resolvedPeriod.endDate}
          compare={search.compare}
          onCompareChange={(compare) =>
            void navigate({ search: (previous) => ({ ...previous, compare }), replace: true })
          }
          asins={asins}
          onAsinsChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, asins: next.length > 0 ? next : undefined }),
              replace: true,
            })
          }
          granularity={search.granularity}
          isLoading={analyticsQuery.isLoading}
          isFetching={analyticsQuery.isFetching}
          isError={analyticsQuery.isError}
          onRetry={() => void analyticsQuery.refetch()}
          adsNotice={
            connection === "ads_missing" ? (
              <div
                role="status"
                className="flex items-center justify-between gap-3 text-xs text-muted-foreground"
              >
                <span className="flex items-center gap-2">
                  <IconBrandAmazon className="size-4 shrink-0" aria-hidden />
                  {canConnect ? t("connect.adsMissing") : t("connect.memberDescription")}
                </span>
                {canConnect ? (
                  <Button size="xs" variant="outline" onClick={handleConnect}>
                    {t("connect.action")}
                  </Button>
                ) : null}
              </div>
            ) : undefined
          }
          freshness={freshnessQuery.data}
          canOpenDataDetails={hasModule("settings")}
          filterNotices={
            data && workspaceShops.length > 1 && data.excludedShops > 0 ? (
              <span className="flex h-[22px] items-center rounded-full border border-line bg-surface px-2 text-badge text-muted-foreground">
                {t("shopScope.excludedShops", { count: data.excludedShops })}
              </span>
            ) : undefined
          }
          targetAcos={aiPolicy.targetAcos}
          performanceAside={
            <AiStatusCard
              className="w-full"
              mode={aiPolicy.mode}
              pendingReviews={data?.aiSummary.pendingDecisions ?? 0}
              nextReviewExpiresAt={data?.aiSummary.nextApprovalExpiresAt ?? null}
              recentRunsIncomplete={runsQuery.data?.hasMore === true || runsQuery.isError}
              issues={activity.failedRuns.length}
              appliedToday={activity.appliedToday}
              autoShareToday={activity.autoShareToday}
              lastRun={activity.lastRun}
              nextRunAt={data?.aiSummary.nextRunAt ?? null}
              periodActions={data?.aiSummary.executedActions ?? 0}
              targetAcos={aiPolicy.targetAcos}
              timeZone={timeZone}
              now={now}
              isLoading={!data || runsQuery.isLoading}
              onManage={
                data && hasPermission("manage_ai") ? () => setAiPolicyOpen(true) : undefined
              }
            />
          }
        />
        <AttentionCard
          items={buildAttentionItems(proposalsQuery.data?.data ?? [], activity.failedRuns)}
          now={now}
          isLoading={proposalsQuery.isLoading || runsQuery.isLoading}
          isError={proposalsQuery.isError || runsQuery.isError}
          incomplete={runsQuery.data?.hasMore === true}
          onRetry={() => {
            void proposalsQuery.refetch();
            void runsQuery.refetch();
          }}
        />
        <TopCampaignsCard
          rows={buildTopCampaignRows(data?.campaigns ?? [], {
            periodDays: periodDayCount(resolvedPeriod.startDate, resolvedPeriod.endDate),
            marketplaceByShop,
            runsByCampaign: recentRunStatusesByCampaign(runsQuery.data?.runs ?? []),
          })}
          totalCount={data?.campaigns.length ?? 0}
          asins={asins}
          currency={data?.currency ?? null}
          targetAcos={aiPolicy.targetAcos}
          isLoading={isLoading}
        />
        <TopAsinsCard
          rows={topAsinRowsFromProductCenter(topAsinsQuery.data?.data ?? [], marketplaceByShop)}
          totalCount={topAsinsQuery.data?.pagination.total ?? 0}
          shopCount={scopeShopIds.length}
          asins={asins}
          currency={topAsinsQuery.data?.currency ?? null}
          targetAcos={aiPolicy.targetAcos}
          isLoading={topAsinsQuery.isLoading}
          isError={topAsinsQuery.isError}
          onRetry={() => void topAsinsQuery.refetch()}
          vendorDataPending={accounts.mix !== "seller"}
        />
        <div className="flex min-w-0 flex-col gap-4 xl:flex-row">
          {scopeShopIds.length > 1 ? (
            <ShopComparisonSection
              workspaceId={user?.activeWorkspaceId ?? null}
              workspaceShops={workspaceShops}
              scopeShopIds={scopeShopIds}
              startDate={resolvedPeriod.startDate}
              endDate={resolvedPeriod.endDate}
              timeZone={timeZone}
              asins={asins}
              rankedShopIds={rankShopsBySpend(data?.campaigns ?? [])}
              className="min-w-0 flex-1"
            />
          ) : null}
          <ConversionEfficiencyCard
            kpis={analyticsQuery.data?.kpis}
            isLoading={analyticsQuery.isLoading}
            isError={analyticsQuery.isError}
            onRetry={() => void analyticsQuery.refetch()}
            className={scopeShopIds.length > 1 ? "xl:w-[380px] xl:shrink-0" : "flex-1"}
          />
        </div>
        {isError && !data ? (
          <ErrorState message={t("loadFailed")} onRetry={() => void refetch()} />
        ) : null}
        {data ? (
          <WorkspaceAiPolicyDialog
            open={aiPolicyOpen}
            onOpenChange={setAiPolicyOpen}
            shopName={currentShop?.shopName ?? t("shopFallback")}
            eligibleCampaigns={data.aiSummary.eligibleCampaigns}
            excludedCampaigns={Math.max(
              0,
              data.campaigns.length - data.aiSummary.eligibleCampaigns,
            )}
            nextRunAt={data.aiSummary.nextRunAt}
          />
        ) : null}
      </DashboardFrame>
      {connectAmazonDialog}
    </>
  );
}

function rankShopsBySpend(campaigns: readonly { shopId: string; metrics: { spend: number } }[]) {
  const spendByShop = new Map<string, number>();
  for (const campaign of campaigns) {
    spendByShop.set(
      campaign.shopId,
      (spendByShop.get(campaign.shopId) ?? 0) + campaign.metrics.spend,
    );
  }
  return [...spendByShop.entries()]
    .filter(([, spend]) => spend > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([shopId]) => shopId);
}
